import { OptionDef } from 'interfaces/options';
import { SecurityClass, SecurityType } from 'lib/enums/security';
import { SecurityDoc, SecurityWhole } from 'lib/interfaces/database/security';
import _ from 'lodash';
import { SecuritiesService } from 'services/securities';
import { create } from 'zustand';

interface State {
  data: SecurityWhole[];

  classOptions: OptionDef[];
  typeOptions: OptionDef[];
}

interface Actions {
  readonly load: (force?: boolean) => void;
  readonly create: (v: Partial<SecurityDoc>) => Promise<boolean>;
  readonly update: (id: string, v: Partial<SecurityDoc>) => Promise<boolean>;
}

interface Store extends State, Actions {}

const useSecuritiesStore = create<Store>((set, get) => {
  const service = SecuritiesService.getInstance();

  return {
    data: [],

    classOptions: [
      { label: 'Debt', value: SecurityClass.Debt },
      { label: 'Equity', value: SecurityClass.Equity },
    ],

    typeOptions: [
      {
        label: 'Debt Mortgage',
        value: SecurityType.DebtMortgage,
        group: SecurityClass.Debt,
      },
      {
        label: 'Debt Other',
        value: SecurityType.DebtOther,
        group: SecurityClass.Debt,
      },
      {
        label: 'Debt Senior',
        value: SecurityType.DebtSenior,
        group: SecurityClass.Debt,
      },
      {
        label: 'Debt Sub',
        value: SecurityType.DebtSub,
        group: SecurityClass.Debt,
      },
      {
        label: 'Equity Common',
        value: SecurityType.EquityCommon,
        group: SecurityClass.Equity,
      },
      {
        label: 'Equity Other',
        value: SecurityType.EquityOther,
        group: SecurityClass.Equity,
      },
      {
        label: 'Equity Pref',
        value: SecurityType.EquityPref,
        group: SecurityClass.Equity,
      },
    ],

    load: async (force) => {
      if (!force && get().data.length > 0) {
        console.debug('Skipped unforced load');
        return;
      }

      const results = await service.search({});

      if (results) {
        set({ data: results });
      }
    },

    create: async (v) => {
      const record = await service.create(v);

      if (record) {
        const nextData = _.clone(get().data);
        nextData.push(record);

        set({ data: nextData });
      }

      return !!record;
    },

    update: async (id, v) => {
      const record = await service.update(id, v);

      if (record) {
        const nextData = get().data.map((d) =>
          d.id === record.id ? record : d
        );

        set({ data: nextData });
      }

      return !!record;
    },
  };
});

export default useSecuritiesStore;
